import { Injectable } from '@angular/core';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import { List as ImmutableList } from 'immutable';
import { RoleType } from '../constants-types/Enums';
import { List } from '../constants-types/PiList.model';
import {
    DreamTeamCrossFunctionId,
    DreamTeamFunctionType,
    TeamImprovementId
} from '../constants-types/StrategyConstants';
import { ModuleName } from '../core-data-model';
import { BulkUpdateType } from '../core-data-model/BulkUpdateApi.connectinterface';
import { FilterInfo, FilterType } from '../core-data-model/FilterInfo.model';
import {
    DreamTeamActionsDefinition,
    Group,
    GroupItemUpdate,
    GroupUpdate,
    GroupValidateResponse
} from '../core-data-model/Group.model';
import {
    SharedGroupsGrantUserAccessResponse,
    SharedGroupsUsersFoldersPost
} from '../core-data-model/GroupsApi.serverinterface';
import { Job } from '../core-data-model/Job.model';
import {
    OrderedSet,
    StandardMaxOrderedSetSize
} from '../core-data-model/OrderedSet';
import { Person } from '../core-data-model/Person.model';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { PizzaPlaygroundSession } from '../core-data-model/Strategy.model';
import { User } from '../core-data-model/User.model';
import { BulkUpdateTransformers } from '../core-services/BulkUpdateApi.datatransform';
import { notification } from '../core-services/Notifications.service';
import {
    ModuleAccessService,
    ModuleUserAccessService
} from '../system-services';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { Logger } from '../system-services/Logger/Logger.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations/Translation.service';
import { BulkUpdateApi } from './BulkUpdateApi.connect';
import { GroupTable } from './GroupTable';
import { PizzaPlaygroundApi } from './PizzaPlaygroundApi.connect';
import { UpdatePizzaPlaygroundSessionPost } from './PizzaPlaygroundApi.serverinterface';
import { SearchStateFactory } from './SearchStateFactory';
import { TeamsApi } from './TeamsApi.connect';

@Injectable({
    providedIn: 'root'
})
export class GroupsDataService {
    // This is our local copy of the groups that can be modified
    // TODO: this concept needs to be pulled out into a reusable class like
    // EditableCollection/EditableList
    allCompanyGroups: { [name: string]: Group } = {}; // ALL groups inside a company, regardless of access
    groups: { [name: string]: Group } = {}; // Groups that are accessible to the current user
    mostRecentGroupIds: OrderedSet<string> = new OrderedSet(
        [],
        StandardMaxOrderedSetSize + 1,
        StandardMaxOrderedSetSize
    );
    groupsPromise: Promise<[Group[], List<PizzaPlaygroundSession>]>;
    isLoading = false;
    // The maximum length of a group name
    groupNameLengthLimit = 50;

    constructor(
        private userProfileSettingsService: UserProfileSettingsService,
        private teamsApi: TeamsApi,
        private bulkUpdateApi: BulkUpdateApi,
        private searchStateFactory: SearchStateFactory,
        private transform: BulkUpdateTransformers,
        private logger: Logger,
        private TR: TranslationService,
        private init: InitDataService,
        private pizzaPlaygroundApi: PizzaPlaygroundApi,
        private moduleAccess: ModuleAccessService,
        private moduleUserAccess: ModuleUserAccessService
    ) {
        this.isLoading = true;
        this.fetchGroups().then(() => {
            this.mostRecentGroupIds.orderedArray = this.fetchMostRecentGroups();
            this.isLoading = false;
        });
    }

    showTeamDiscovery(group: Group) {
        return (
            this.moduleAccess.hasDesign &&
            this.isEditableByUser(group) &&
            this.init.user.hasPermissionLevelOrGreater(RoleType.User)
        );
    }

    nameExists(matchName: string, myGroup?: Group): boolean {
        return !!this.getCompleteGroup().find(
            (group) =>
                group.name?.toLocaleLowerCase().trim() ===
                    matchName.toLocaleLowerCase().trim() &&
                !(myGroup && group.id === myGroup.id)
        );
    }

    nameLengthExceeds(matchName: string): boolean {
        return matchName.length > this.groupNameLengthLimit;
    }

    isGroupOwner(group: Group | string) {
        const groupOwnerId =
            group && group instanceof Group ? group.groupOwnerId : group;
        return groupOwnerId === this.init.user.userGuid;
    }

    isEditableByUser(group: Group | string) {
        const userCanEdit =
            group && group instanceof Group ? group.isEditableByUser : group;
        const isGroupOwner = this.isGroupOwner(group);
        /**
         * Adding this.isDesignSelfServeAdminOrAbove as a check so that give all self serve users edit access.
         * Currently we do not have a good way to accomplish this on the backend while isolating to self serve only
         * Once a better solution is identified we will switch
         */
        return (
            userCanEdit || isGroupOwner || this.isDesignSelfServeAdminOrAbove
        );
    }

    public makeGroupTable(group: Group) {
        return new GroupTable(
            group,
            this.teamsApi,
            this.searchStateFactory,
            this.TR
        );
    }

    private fetchGroups() {
        // When fetching teams we want to identify if they have an active Team Discovery Session and if so what their team type is.
        // A non-null value for team type therefore tells us that this team has an active Team Discovery session with and references the team type.
        this.isLoading = true;

        /**
         * Adding this as a check so that we can pull down all company groups as shared groups.
         * Currently we do not have a good way to accomplish this on the backend while isolating to self serve only
         * Once a better solution is identified we will switch
         */
        const teamsPromise =
            this.isDesignSelfServeAdminOrAbove || this.hasDesignLimitedAccess()
                ? this.teamsApi.getAllGroupsInCompany(
                      this.userProfileSettingsService.currentCompany
                  )
                : this.teamsApi.getSharedGroupsForUser(
                      this.userProfileSettingsService.currentCompany
                  );

        const allTeamsPromise = this.init.hasPermissionLevelOrAbove(
            RoleType.AccountAdmin
        )
            ? this.teamsApi.getAllGroupsInCompany(
                  this.userProfileSettingsService.currentCompany
              )
            : null; // only make this call if AccountAdmin or above
        const sessionsPromise = this.getTeamDiscoverySessionsPromise();
        this.groupsPromise = Promise.all([
            sessionsPromise,
            teamsPromise,
            allTeamsPromise
        ]).then(([sessions, sharedGroups, allCompanyGroups]) => {
            this.allCompanyGroups = {};
            this.groups = {};

            const sessionsTable = {};
            sessions.forEach((session) => {
                sessionsTable[session.teamId] = session;
            });
            allCompanyGroups?.forEach((companyGroup) => {
                if (sessionsTable[companyGroup.id]) {
                    companyGroup.hasTeamDiscoverySession = true;
                }
                if (!(companyGroup.id in this.groups)) {
                    companyGroup.isHidden = true;
                }
                this.allCompanyGroups[companyGroup.id] = companyGroup;
            });
            sharedGroups.forEach((sharedGroup) => {
                if (sessionsTable[sharedGroup.id]) {
                    sharedGroup.hasTeamDiscoverySession = true;
                }
                this.groups[sharedGroup.id] = sharedGroup;
                // Because the allTeamsPromise does not return data on whether the Team is shared with the current user,
                // we must default to the information provided from the teamsPromise. We override the value below
                this.allCompanyGroups[sharedGroup.id] = sharedGroup;
            });
            this.isLoading = false;
            return [sharedGroups, sessions];
        });
        return this.groupsPromise;
    }

    private async getTeamDiscoverySessionsPromise() {
        // We only want to make the API call to retrieve Team Discovery Sessions if the user is more privileged than ReadOnly since otherwise the endpoint will return a 403 error.
        // ReadOnly users cannot access Team Discovery anyway so there is no need to fetch this data in this case.
        if (this.init.user.hasPermissionLevelOrGreater(RoleType.User)) {
            return this.pizzaPlaygroundApi.getPizzaPlaygroundSessions();
        }

        return ImmutableList<PizzaPlaygroundSession>();
    }

    private fetchMostRecentGroups(): string[] {
        const serverRecentGroupIdsArray =
            this.userProfileSettingsService.companySpecificSetting.recentlySelectedGroupIds.getArray();
        const foundGroupIds = serverRecentGroupIdsArray.filter(
            (groupId) => !!this.groups[groupId]
        );

        if (serverRecentGroupIdsArray.length !== foundGroupIds.length) {
            this.logger.logError(
                'Unfound <Most Recent Groups>',
                serverRecentGroupIdsArray.filter(
                    (groupId) => !this.groups[groupId]
                )
            );
        }

        return foundGroupIds;
    }
    getGroupsWithNameMatch(
        match: string,
        groups: Group[] = this.getCompleteGroup()
    ): Group[] {
        const matchLowerCase = match.toLocaleLowerCase();
        return groups.filter((g) =>
            g.name.toLocaleLowerCase().includes(matchLowerCase)
        );
    }

    getGroup(groupId: string): Group {
        return this.groups[groupId];
    }
    getAllIds(): string[] {
        return Object.keys(this.groups);
    }
    // Get all the groups (TODO: rename this to be less confusingly worded)
    getCompleteGroup(): Group[] {
        return Object.values(this.groups);
    }
    getCompanyGroups(): Group[] {
        if (this.init.hasPermissionLevelOrAbove(RoleType.AccountAdmin)) {
            return Object.values(this.allCompanyGroups);
        }
        return this.getCompleteGroup();
    }

    async getPizzaPlaygroundSessionByTeamId(
        teamId: string
    ): Promise<PizzaPlaygroundSession> {
        return this.pizzaPlaygroundApi.getPizzaPlaygroundSessionByTeamId(
            teamId
        );
    }

    addEntitiesToGroup(group: Group, entities: SearchResult[]) {
        const myEntities = this.transform.transformBulkUpdateEntries(entities);
        return this.bulkUpdateApi
            .bulkUpdate(BulkUpdateType.AddToTeam, myEntities, group.id)
            .then(async (...args) => {
                await this.fetchGroups();
                this.updateMostRecentGroups(group.id);
                // Update the people in the updated group
                // TODO: do this less lazily
                return args;
            });
    }

    private searchResultToRemoveEntityFromGroup(entity: SearchResult): {
        id: string;
        isPerson: boolean;
    } {
        return {
            id: entity.id,
            isPerson: entity instanceof Person
        };
    }

    removeEntitiesFromGroup(team: Group, entities: SearchResult[]) {
        const entitiesToServer = entities.map((e) =>
            this.searchResultToRemoveEntityFromGroup(e)
        );
        return this.bulkUpdateApi
            .bulkUpdate(
                BulkUpdateType.RemoveFromTeam,
                entitiesToServer,
                team.id
            )
            .then(async (...args) => {
                // Update the people in the updated group
                // TODO: do this less lazily
                await this.fetchGroups();
                return args;
            });
    }

    async updateGroup(team: GroupUpdate) {
        if (team.teamFunctionType !== DreamTeamFunctionType.Other) {
            team.otherTeamFunction = null;
        }
        if (!team.teamImprovementIds.includes(TeamImprovementId.Other)) {
            team.otherTeamImprovement = null;
        }
        await this.teamsApi.updateTeam(team);
        await Promise.all([
            this.updateMostRecentGroups(team.originalObject.id),
            this.fetchGroups()
        ]);
    }

    async updateGroupItem(teamId: string, teamItem: GroupItemUpdate) {
        await this.teamsApi.updateTeamItem(teamId, teamItem);
    }

    async updateGroupItemSPS(
        sessionId: string,
        sessionUpdate: UpdatePizzaPlaygroundSessionPost
    ) {
        await this.pizzaPlaygroundApi.updatePizzaPlaygroundSession(
            sessionId,
            sessionUpdate
        );
    }

    private removeFromRecentGroups(group: Group): void {
        this.mostRecentGroupIds.removeElement(
            (recentGroupId) => recentGroupId === group.id
        );
    }

    /**
     * Deletes the given group. This persists to the server.
     *
     * @param group
     */
    deleteGroup(group: Group): Promise<any> {
        return this.teamsApi.deleteTeam(group).then((...args) => {
            this.fetchGroups();
            this.removeFromRecentGroups(group);

            return args;
        });
    }

    createGroup(
        groupName: string,
        teamFunctionType: DreamTeamFunctionType,
        otherTeamFunction: string,
        crossFunctionId: DreamTeamCrossFunctionId,
        teamImprovementIds: TeamImprovementId[],
        otherTeamImprovement: string
    ): Promise<Group> {
        return this.teamsApi
            .createTeam(
                groupName,
                teamFunctionType,
                otherTeamFunction,
                crossFunctionId,
                teamImprovementIds,
                otherTeamImprovement
            )
            .then((group) => {
                // Replace our local copy with the newly created one from server
                this.fetchGroups().then(() => {
                    this.updateMostRecentGroups(group.id);
                });
                // Had to do this to ensure team could be found when created within design self serve paywall modal
                this.groups[group.id] = group;
                return group;
            });
    }

    removeGroupByName(group: Group) {
        const groupsIndex = Object.values(this.groups).find(
            (g) => g.name === group.name
        ).id;
        delete this.groups[groupsIndex];
    }

    updateMostRecentGroups(groupId: string): void {
        this.mostRecentGroupIds.updateArrayTop(groupId);
        this.userProfileSettingsService.updateMostRecentGroups(
            this.mostRecentGroupIds.orderedArray
        );
    }

    getTDSessionStatusText(group: Group) {
        return group.hasTeamDiscoverySession
            ? this.TR.key('teams.resumeTeamDiscovery')
            : this.TR.key('teams.startTeamDiscovery');
    }

    getGroupContentsByGroup(
        group: Group,
        searchFilters?: FilterInfo[]
    ): Promise<(Person | Job)[]> {
        let searchState = this.searchStateFactory
            .newSearchState()
            .setPageSize(group.peopleAndJobCount);
        if (searchFilters) {
            searchState = searchState.addFilters(searchFilters);
        } else {
            searchState = searchState.addFilters([
                new FilterInfo({ type: FilterType.People })
            ]);
        }

        return this.teamsApi.getGroupContents(group.id, searchState);
    }

    validateGroups(
        userId: string,
        groupId: string
    ): Promise<GroupValidateResponse[]> {
        const payload = {
            userCompanyMyGroupIdList: [
                {
                    uniqueId: groupId
                }
            ],
            userIdList: [
                {
                    uniqueId: userId
                }
            ]
        };

        return this.teamsApi.validateSharedGroups(payload);
    }

    giveUsersAccessToGroup(
        usersFolders: SharedGroupsUsersFoldersPost
    ): Promise<SharedGroupsGrantUserAccessResponse> {
        return this.teamsApi.grantUsersAccessToSharedGroup(usersFolders);
    }

    revokeUsersAccessToGroup(userAccess: SharedGroupsUsersFoldersPost) {
        return this.teamsApi.revokeUserAccessToSharedGroup(userAccess);
    }

    @notification('notification.title-success')
    getSharedGroupsForUser(companyId: string): Promise<Group[]> {
        return this.teamsApi.getSharedGroupsForUser(companyId);
    }

    async getUsersWithGroupAccess(groupId: string): Promise<User[]> {
        return this.teamsApi.getUsersWithGroupAccess(groupId);
    }

    async getSessionByTeamId(teamID: string): Promise<PizzaPlaygroundSession> {
        return this.pizzaPlaygroundApi.getPizzaPlaygroundSessionByTeamId(
            teamID
        );
    }

    getDreamTeamActionsDefinition(): Promise<DreamTeamActionsDefinition> {
        return this.teamsApi.getDreamTeamActionsDefinition();
    }

    hasSummaryAccess(sessionId: string): Promise<boolean> {
        return this.pizzaPlaygroundApi.hasSummaryAccess(sessionId);
    }

    /**
     * Adding this as a check so that we can pull down all company groups as shared groups.
     * Currently we do not have a good way to accomplish this on the backend while isolating to self serve only
     * Once a better solution is identified we will switch
     */
    private get isDesignSelfServeAdminOrAbove(): boolean {
        return (
            this.moduleAccess.isTeamDiscoverySelfServe &&
            this.init.hasPermissionLevelOrAbove(RoleType.AccountAdmin)
        );
    }

    /**
     *
     * @returns Checks if user has access to Design and atleast limited access
     */
    private hasDesignLimitedAccess(): boolean {
        if (
            this.moduleAccess.hasDesign1 &&
            !this.moduleAccess.isTeamDiscoverySelfServe
        ) {
            return false;
        } else {
            // In this case, Permissions Doesn't know about "Design2" as a Module it just knows that it should do the check
            // for ABAC users permissions level in Design.
            // As long as the above doesn't return true this will hold true
            // Which with recent changes to identifing Design1 vs. Design2 should force the issue
            return this.moduleUserAccess.hasModuleAccessLevelOrAbove(
                ModuleName.Design,
                PermissionsModuleAccessLevel.LimitedAccess
            );
        }
    }
}
